// [S-061c] Brief detail — renders L1 (Standard market terms) and L2
// (Our precedents) sections side-by-side, never blended (Master Rule
// 1.6). Each section is its own <article> with its own citation
// block. The L2 section degrades to a "Precedent unavailable"
// note when null (per Brief §2.5).
import { Link, useParams } from "react-router-dom";
import { Alert, SectionHead } from "../components";
import {
  useBrief,
  type Brief,
  type BriefL1Section,
  type BriefL2Section,
} from "../data/briefsQuery";
import { talentTierLabel } from "../lib/talentTier";

export function BriefDetail(): JSX.Element {
  const { briefId } = useParams<{ briefId: string }>();
  const { data, isLoading, isError, error } = useBrief(briefId);

  if (isLoading) {
    return (
      <div className="prec__state" role="status" aria-live="polite">
        Loading brief…
      </div>
    );
  }

  if (isError) {
    return (
      <Alert label="Couldn't load the brief" tone="error">
        {error.message}
        <p>
          <Link to="/briefs">← back to briefs</Link>
        </p>
      </Alert>
    );
  }

  if (data === undefined) {
    return (
      <div className="prec__state">
        <div className="prec__state__title">Brief not found</div>
        <p>
          <Link to="/briefs">← back to briefs</Link>
        </p>
      </div>
    );
  }

  return <BriefView brief={data} />;
}

function BriefView({ brief }: { brief: Brief }): JSX.Element {
  return (
    <article className="brf__doc" data-testid="brief-doc">
      <header className="brf__doc__head">
        <div className="brf__doc__crumb">
          <Link to="/briefs">← all briefs</Link>
        </div>
        <h1 className="brf__doc__title">
          {prettyGuild(brief.input.guild)} ·{" "}
          {prettyDealType(brief.input.dealType)} ·{" "}
          {prettyTier(brief.input.talentTier)}
        </h1>
        <dl className="brf__doc__meta">
          <div>
            <dt>Composed</dt>
            <dd>{formatDate(brief.composedAt)}</dd>
          </div>
          <div>
            <dt>Posture</dt>
            <dd>{brief.input.opposingCounselPosture}</dd>
          </div>
          <div>
            <dt>Key terms</dt>
            <dd>{brief.input.keyTermsInFlight}</dd>
          </div>
        </dl>
      </header>

      <section
        className="brf__section brf__section--l1"
        data-testid="brief-l1"
        aria-labelledby="brief-l1-title"
      >
        <SectionHead
          id="brief-l1-title"
          title={
            <>
              <span className="src-dot cert" aria-hidden="true" />
              Standard market terms
            </>
          }
          action={<span className="brf__section__layer">Templates · L1</span>}
        />
        {brief.l1Section === null ? (
          <p className="brf__section__empty">
            No certified template matches{" "}
            {prettyGuild(brief.input.guild)} ·{" "}
            {prettyDealType(brief.input.dealType)} ·{" "}
            {prettyTier(brief.input.talentTier)}.
          </p>
        ) : (
          <L1Body section={brief.l1Section} />
        )}
      </section>

      <section
        className="brf__section brf__section--l2"
        data-testid="brief-l2"
        aria-labelledby="brief-l2-title"
      >
        <SectionHead
          id="brief-l2-title"
          title={
            <>
              <span className="src-dot stud" aria-hidden="true" />
              Our precedents
            </>
          }
          action={<span className="brf__section__layer">Precedent · L2</span>}
        />
        {brief.l2Section === null ? (
          <p
            className="brf__section__empty"
            data-testid="brief-l2-unavailable"
          >
            Precedent unavailable — your organization's Layer 2 index
            isn't deployed yet. The standard market terms above stand
            on their own; precedents will appear here once L2 is live.
          </p>
        ) : (
          <L2Body section={brief.l2Section} />
        )}
      </section>
    </article>
  );
}

function L1Body({ section }: { section: BriefL1Section }): JSX.Element {
  return (
    <>
      <ul className="brf__bullets">
        {section.bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
      <footer className="brf__section__cite">
        <span>{section.citationLabel}</span>
      </footer>
    </>
  );
}

function L2Body({ section }: { section: BriefL2Section }): JSX.Element {
  return (
    <>
      <ul className="brf__bullets">
        {section.bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
      <footer className="brf__section__cite">
        <span>{section.citationLabel}</span>
        <span className="brf__section__id">{section.dateRange}</span>
      </footer>
    </>
  );
}

function prettyGuild(g: string): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function prettyDealType(d: string): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function prettyTier(t: string): string {
  return talentTierLabel(t);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
